import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { List } from 'react-window';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const resolveField = (field, item) => {
  if (!field || !item) return '';
  if (item[field] !== undefined) return item[field];
  const key = Object.keys(item).find(k => k.toLowerCase() === field.toLowerCase());
  return key ? item[key] : '';
};

// Row renderer defined outside component for stable reference
const Row = ({ index, style, items, selectedItem, onSelectItem, primaryField, secondaryField, secondaryFormatter }) => {
  const item = items[index];
  const isSelected = selectedItem?.id === item.id;
  const primary = resolveField(primaryField, item) || '';
  let secondary = '';
  if (secondaryFormatter) {
    secondary = secondaryFormatter(item);
  } else if (secondaryField) {
    secondary = resolveField(secondaryField, item) || '';
  }
  return (
    <div style={{ ...style, overflow: 'hidden' }}>
      <button
        onClick={() => onSelectItem(item)}
        className={`list-item-btn ${isSelected ? 'selected' : ''}`}
      >
        <span className="list-item-primary">{primary}</span>
        {secondary && <span className="list-item-secondary">{secondary}</span>}
      </button>
    </div>
  );
};

const ListPanel = ({
  items,
  selectedItem,
  onSelectItem,
  primaryField,
  secondaryField,
  secondaryFormatter,
  collapsed,
  onToggleCollapse,
  scrollToSelected = false
}) => {
  const listRef = useRef(null);

  // Scroll to selected item on deep link navigation
  useEffect(() => {
    if (scrollToSelected && selectedItem && listRef.current) {
      const index = items.findIndex(item => item.id === selectedItem.id);
      if (index >= 0) {
        setTimeout(() => {
          listRef.current?.scrollToRow({ index, align: 'start' });
        }, 100);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToSelected, selectedItem?.id]);

  // Keyboard navigation — up/down arrows move between items
  const handleKeyDown = useCallback((e) => {
    if (!items || items.length === 0) return;
    const currentIndex = items.findIndex(item => item.id === selectedItem?.id);
    let newIndex = currentIndex;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : currentIndex;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
    } else {
      return;
    }

    if (newIndex !== currentIndex && items[newIndex]) {
      onSelectItem(items[newIndex]);
      listRef.current?.scrollToRow({ index: newIndex, align: 'auto' });
    }
  }, [items, selectedItem, onSelectItem]);

  // Memoize rowProps to avoid unnecessary row re-renders
  const rowProps = useMemo(() => ({
    items,
    selectedItem,
    onSelectItem,
    primaryField,
    secondaryField,
    secondaryFormatter,
  }), [items, selectedItem, onSelectItem, primaryField, secondaryField, secondaryFormatter]);

  if (collapsed) {
    return (
      <div className="list-panel-collapsed">
        <button
          onClick={onToggleCollapse}
          className="list-panel-expand-btn"
          title="Expand list"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div
      className="list-panel"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="list-panel-header">
        <button
          onClick={onToggleCollapse}
          className="list-panel-collapse-btn"
          title="Collapse list"
        >
          <ChevronLeft size={16} />
        </button>
      </div>
      <div className="list-scroll">
        <List
          listRef={listRef}
          rowComponent={Row}
          rowCount={items.length}
          rowHeight={44}
          rowProps={rowProps}
          style={{ height: '100%' }}
        />
      </div>
    </div>
  );
};

export default ListPanel;
